/**
 * US-E1-003 — Set new password (B-2 from protocol №031).
 * Backend: POST /api/v1/auth/reset { token, new_password } → 204.
 * Errors: 410 token_expired/token_invalid · 400 weak_password.
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import { ApiError } from '@/api/client'
import { Icon } from '@/components/icons/Icon'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { PasswordStrengthMeter } from '@/components/ui/PasswordStrengthMeter'
import { Button } from '@/components/ui/Button'
import { Banner } from '@/components/ui/Banner'

export function Reset() {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [clientError, setClientError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (body: { token: string; new_password: string }) => authApi.reset(body),
  })

  // 1. State: token missing in URL
  if (!token) {
    return (
      <AuthLayout side="login">
        <h1 className="text-3xl font-bold font-display text-text-primary mb-3 text-center lg:text-left">Ссылка некорректна 🚫</h1>
        <p className="text-text-secondary mb-8 text-center lg:text-left">
          В ссылке отсутствует токен. Запроси новую для сброса пароля.
        </p>
        <a href="/auth/recover" className="inline-flex items-center gap-2 text-text-link font-medium">
          <Icon name="arrow-left" size={16} aria-hidden="true" />
          Запросить новую ссылку
        </a>
      </AuthLayout>
    )
  }

  // 2. State: success — redirect to login
  if (mutation.isSuccess) {
    return (
      <AuthLayout side="login">
        <div className="text-center lg:text-left">
          <div className="w-16 h-16 rounded-full bg-status-success-bg flex items-center justify-center mb-6 mx-auto lg:mx-0">
            <Icon name="check-circle" size={32} aria-hidden="true" className="text-status-success-text" />
          </div>
          <h1 className="text-3xl font-bold font-display text-text-primary mb-3">Пароль обновлён 🔓</h1>
          <p className="text-text-secondary mb-8">
            Используй новый пароль для входа.
          </p>
          <Button asChild variant="primary" size="lg">
            <a href="/auth/login">Войти</a>
          </Button>
        </div>
      </AuthLayout>
    )
  }

  const onSubmit = (ev: React.FormEvent) => {
    ev.preventDefault()
    setClientError(null)
    if (password.length < 10) {
      setClientError('Минимум 10 символов')
      return
    }
    if (password !== confirm) {
      setClientError('Пароли не совпадают')
      return
    }
    mutation.mutate({ token, new_password: password })
  }

  // Map server error.code → user-facing message
  const serverError = (() => {
    if (!mutation.isError) return null
    if (mutation.error instanceof ApiError) {
      switch (mutation.error.code) {
        case 'token_expired':
          return {
            title: 'Ссылка истекла',
            body: 'Ссылка для сброса пароля действует 1 час с момента отправки. Запроси новую.',
            cta: { href: '/auth/recover', label: 'Запросить новую ссылку' },
          }
        case 'token_invalid':
          return {
            title: 'Ссылка некорректна',
            body: 'Возможно, ссылка уже использована или повреждена. Запроси новую.',
            cta: { href: '/auth/recover', label: 'Запросить новую ссылку' },
          }
        case 'weak_password':
          return { inline: 'Пароль слишком слабый — минимум 10 символов, буквы и цифры' }
        default:
          return { inline: mutation.error.message || 'Что-то пошло не так. Попробуй ещё раз.' }
      }
    }
    return { inline: 'Сетевая ошибка. Проверь интернет и попробуй ещё раз.' }
  })()

  // 3. State: full-page expired/invalid (no retry)
  if (serverError && 'title' in serverError && serverError.title) {
    const fullErr = serverError as { title: string; body: string; cta: { href: string; label: string } }
    return (
      <AuthLayout side="login">
        <div className="text-center lg:text-left">
          <div className="w-16 h-16 rounded-full bg-status-warning-bg flex items-center justify-center mb-6 mx-auto lg:mx-0">
            <Icon name="alert-triangle" size={32} aria-hidden="true" className="text-status-warning-text" />
          </div>
          <h1 className="text-3xl font-bold font-display text-text-primary mb-3">{fullErr.title}</h1>
          <p className="text-text-secondary mb-8">{fullErr.body}</p>
          <a href={fullErr.cta.href} className="inline-flex items-center gap-2 text-text-link font-medium">
            <Icon name="arrow-left" size={16} aria-hidden="true" />
            {fullErr.cta.label}
          </a>
        </div>
      </AuthLayout>
    )
  }

  // 4. State: form
  return (
    <AuthLayout side="login">
      <h1 className="text-3xl font-bold font-display text-text-primary mb-2 text-center lg:text-left">Новый пароль 🔒</h1>
      <p className="text-text-secondary mb-10 text-center lg:text-left">Придумай надёжный пароль и подтверди.</p>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Hidden username field — Chromium password-manager hint (BUG-03 fix). */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          readOnly
          value={new URLSearchParams(window.location.search).get('email') ?? ''}
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
        />
        <div>
          <label htmlFor="reset-new-password" className="block text-sm font-medium text-text-primary mb-2">Новый пароль</label>
          <PasswordInput
            id="reset-new-password"
            name="new-password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            placeholder="Минимум 10 символов"
            leftSlot={<Icon name="lock" size={20} />}
            hasError={!!(clientError || (serverError && 'inline' in serverError))}
            aria-describedby={clientError || (serverError && 'inline' in serverError) ? 'reset-error' : 'reset-password-meter'}
          />
          <div id="reset-password-meter" className="mt-2">
            <PasswordStrengthMeter value={password} />
          </div>
        </div>

        <div>
          <label htmlFor="reset-confirm" className="block text-sm font-medium text-text-primary mb-2">Повтори пароль</label>
          <PasswordInput
            id="reset-confirm"
            name="new-password-confirm"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            placeholder="Повтори новый пароль"
            leftSlot={<Icon name="lock" size={20} />}
            hasError={!!(clientError && password !== confirm)}
          />
        </div>

        {(clientError || (serverError && 'inline' in serverError)) && (
          <div id="reset-error">
            <Banner variant="error" body={clientError || (serverError as { inline: string }).inline} />
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" fullWidth loading={mutation.isPending} disabled={!password || !confirm}>
          {mutation.isPending ? 'Сохраняем…' : 'Сохранить пароль'}
        </Button>

        <div className="pt-2">
          <a href="/auth/login" className="text-sm text-text-link font-medium">
            Вернуться к входу
          </a>
        </div>
      </form>
    </AuthLayout>
  )
}
